/**
 * src/pages/OriginAndBeyond/sections/CosmicTimeRibbon.tsx
 * Cosmic Time Ribbon connecting all four questions along a ScrubTimeline:
 * 1. Big Bang (~13.8 billion years ago, established) -> Origin
 * 2. Earth Accretion (~4.54 Gyr ago, established) -> Origin
 * 3. Earliest Terrestrial Life (at least ~3.5 Gyr ago, established) -> Origin
 * 4. The Observer: Emergence of Conscious Inquiry (~300,000 yr ago, established) -> Consciousness
 * 5. Entropy & Transience: Every Life Ends (Present & Continuous, established) -> Death
 * 6. Sun's Red Giant Phase (~5 billion years from now, established) -> Future
 * 7. Accelerating Expansion & Dark Energy (Present to Future, established) -> Future
 * 8. The Degenerate Era & Final Stars (~10^14 years, research) -> Future
 *
 * Footnote numbers [1..7] reference the hub's own SourceList.
 */

import React from "react";
import { Clock, ExternalLink } from "lucide-react";
import { Link } from "@/components/common/Link";
import { ScrubTimeline } from "@/components/originBeyond/ScrubTimeline";
import { HUB_TIMELINE_EVENTS } from "../originAndBeyondData";
import "@/components/cosmic/cosmic.css";
export function CosmicTimeRibbon() {
  return <section className="w-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16" aria-labelledby="timeline-heading">
      <div className="text-center max-w-2xl mx-auto mb-10 sm:mb-12">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/20 bg-primary/5 text-primary text-xs font-mono mb-3">
          <Clock className="size-3.5" aria-hidden="true" />
          <span>CHRONOLOGY OF INQUIRY</span>
        </div>
        <h2 id="timeline-heading" className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground mb-3">
          The Cosmic Time Ribbon
        </h2>
        <p className="text-sm sm:text-base text-muted-foreground font-sans leading-relaxed">
          From primordial quantum fluctuations to the final stellar embers across deep time,
          trace how physical cosmology, conscious observers, mortality, and cosmic dissolution
          converge along a single evolutionary arc.
        </p>
      </div>

      {/* ScrubTimeline Interactive Component */}
      <ScrubTimeline events={HUB_TIMELINE_EVENTS} ariaLabel="Cosmic time ribbon scrubbing through 13.8 billion years to the deep future" trackLabelStart="13.8 Gyr Ago (Big Bang)" trackLabelEnd="10¹⁴ Years (Degenerate Era)" />

      {/* Cross-Question Quick Routing Matrix */}
      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="ql-card p-4 rounded-xl flex flex-col justify-between">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-primary font-semibold">
              Events 1–3
            </span>
            <h3 className="font-serif text-sm font-bold text-foreground mt-1 mb-1">
              Cosmic Origins
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Explore the Planck era, nucleosynthesis, and prebiotic chemistry.
            </p>
          </div>
          <Link to="/WhereDidWeComeFromPage" className="mt-3 inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline">
            <span>Where Did We Come From?</span>
            <ExternalLink className="size-3" />
          </Link>
        </div>

        <div className="ql-card p-4 rounded-xl flex flex-col justify-between">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-primary font-semibold">
              Event 4
            </span>
            <h3 className="font-serif text-sm font-bold text-foreground mt-1 mb-1">
              The Conscious Mind
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              How reflective awareness arose ~300,000 years ago in modern humans.
            </p>
          </div>
          <Link to="/WhatIsConsciousnessPage" className="mt-3 inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline">
            <span>What Is Consciousness?</span>
            <ExternalLink className="size-3" />
          </Link>
        </div>

        <div className="ql-card p-4 rounded-xl flex flex-col justify-between">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-primary font-semibold">
              Event 5
            </span>
            <h3 className="font-serif text-sm font-bold text-foreground mt-1 mb-1">
              Biological Transience
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Thermodynamic entropy, resuscitation research, and mortality.
            </p>
          </div>
          <Link to="/WhatHappensAfterDeathPage" className="mt-3 inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline">
            <span>What Happens After Death?</span>
            <ExternalLink className="size-3" />
          </Link>
        </div>

        <div className="ql-card p-4 rounded-xl flex flex-col justify-between">
          <div>
            <span className="font-mono text-[10px] uppercase tracking-wider text-primary font-semibold">
              Events 6–8
            </span>
            <h3 className="font-serif text-sm font-bold text-foreground mt-1 mb-1">
              Deep Future
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Solar red-giant phase, dark energy, and the extinction of stars.
            </p>
          </div>
          <Link to="/WhereIsEverythingGoingPage" className="mt-3 inline-flex items-center gap-1 text-xs font-mono text-primary hover:underline">
            <span>Where Is Everything Going?</span>
            <ExternalLink className="size-3" />
          </Link>
        </div>
      </div>
    </section>;
}